import React from "react";
import { Box, ButtonBase, Typography } from "@mui/material";
import {
  IoBulbOutline,
  IoHelpCircleOutline,
  IoDocumentTextOutline,
  IoCalendarOutline,
  IoArrowDown
} from "react-icons/io5";
import ChatMessage from "./ChatMessage";
import { adminColors, radius, TOUCH_HIT_SX } from "../../theme/adminTheme";

// Starter prompts for the empty-conversation state. Each `prompt` is sent
// as-is via onStarterPrompt, so keep them phrased as something a student
// would actually type as their first message.
const STARTER_PROMPTS = [
  {
    icon: IoBulbOutline,
    label: "Explain a concept",
    detail: "Break down something you're stuck on",
    prompt: "Can you help me understand a concept I'm stuck on?"
  },
  {
    icon: IoHelpCircleOutline,
    label: "Quiz me on a topic",
    detail: "Practice with questions, one at a time",
    prompt: "Quiz me on a topic — ask me questions one at a time."
  },
  {
    icon: IoDocumentTextOutline,
    label: "Check my essay",
    detail: "Get feedback before you submit",
    prompt: "I'd like you to review an essay I've written and give feedback."
  },
  {
    icon: IoCalendarOutline,
    label: "Plan revision",
    detail: "Build a study plan before your exams",
    prompt: "Help me build a revision plan for my upcoming exams."
  }
];

const ChatArea = ({
  chatBoxRef,
  chatHistory,
  editingMessageId,
  editingContent,
  setEditingContent,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onDelete,
  onFileDelete,
  onFileDownload,
  // Optional: called with a starter prompt's text when a student taps one
  // of the cards below. Wired up from Chat.js via handleStarterPrompt,
  // which stages the text into the composer and defers sendMessage() by
  // one tick so useChatActions picks up the fresh input (see the comment
  // on handleStarterPrompt in Chat.js for why).
  onStarterPrompt,
  // Both from useChatHistory.js (via Chat.js): isNearBottom is the
  // rAF-throttled mirror of its scroll-position tracking, used only to
  // show/hide the "jump to latest" pill below (not the auto-scroll
  // decision itself, which reads the untharottled ref). onJumpToLatest
  // scrolls to bottom and resets that tracking immediately.
  isNearBottom = true,
  onJumpToLatest
}) => {
  // The last message streaming/loading in is the one case worth a
  // different pill label ("new content arriving" vs. "you're behind") -
  // deliberately not an unread count, which would need real per-message
  // read-tracking this app doesn't have.
  const lastMessage = chatHistory[chatHistory.length - 1];
  const isStreamingNow = Boolean(lastMessage?.isLoading || lastMessage?.isStreaming);
  const showJumpToLatest = chatHistory.length > 0 && !isNearBottom;

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        position: "relative",
        "@media (prefers-reduced-motion: reduce)": {
          "*": {
            transitionDuration: "0.01ms !important",
            animationDuration: "0.01ms !important",
          },
        },
      }}
    >
      <Box
        ref={chatBoxRef}
        sx={{
          // The parent chain (Chat.js) is a dvh-safe flex column with
          // minHeight: 0 at every level, so flex: 1 here already resolves to
          // "whatever's left after the header and composer" — including when
          // the composer grows to its 8-row max or the header wraps. A fixed
          // maxHeight (e.g. calc(100vh - 200px)) would fight that and either
          // clip messages or leave dead space, so height is left to flex.
          height: "100%",
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          backgroundColor: adminColors.surface,
          py: 3,
          px: 0,
          // Deliberately no CSS scroll-behavior: 'smooth' here. Per the CSSOM
          // View spec, a JS scrollTo()/scrollTop call with behavior:'auto'
          // (the default) doesn't mean "instant" - it means "defer to this
          // element's CSS scroll-behavior", so a permanent smooth here was
          // silently overriding useChatHistory's per-token instant scroll and
          // reintroducing the exact "restarts a smooth animation on every
          // token" jank that fix was for. scrollToBottom (useChatHistory.js)
          // is the single place that decides smooth vs. instant per call now.
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#d1d5db',
            borderRadius: '3px',
            '&:hover': {
              background: '#9ca3af',
            }
          },
        }}
      >
        {chatHistory.length === 0 ? (
          // Empty state — greets the student and offers a few concrete
          // starting points instead of a blank box and a generic prompt.
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "50vh",
              textAlign: "center",
              px: { xs: 2, sm: 3, md: 4 }
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "12px",
                backgroundColor: adminColors.accentLight,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2.5
              }}
            >
              <IoBulbOutline size={22} color={adminColors.accent} />
            </Box>

            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: adminColors.ink,
                mb: 1,
                fontSize: { xs: '1.375rem', sm: '1.75rem' },
                letterSpacing: "-0.01em"
              }}
            >
              What are we working on today?
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: adminColors.inkSoft,
                maxWidth: "440px",
                lineHeight: 1.6,
                mb: 4
              }}
            >
              Pick a starting point, or just type below.
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                gap: 1.5,
                width: "100%",
                maxWidth: "560px"
              }}
            >
              {STARTER_PROMPTS.map(({ icon: Icon, label, detail, prompt }, i) => (
                <ButtonBase
                  key={label}
                  onClick={() => onStarterPrompt?.(prompt)}
                  focusRipple
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    textAlign: "left",
                    p: 2,
                    borderRadius: "10px",
                    border: `1px solid ${adminColors.accentBorder}`,
                    backgroundColor: adminColors.surfaceRaised,
                    opacity: 0,
                    animation: `chatStarterIn 320ms cubic-bezier(0.23, 1, 0.32, 1) ${i * 50}ms forwards`,
                    "@keyframes chatStarterIn": {
                      from: { opacity: 0, transform: "translateY(6px)" },
                      to: { opacity: 1, transform: "translateY(0)" },
                    },
                    transition: "border-color 150ms ease, background-color 150ms ease, transform 120ms ease-out",
                    "&:hover": {
                      borderColor: adminColors.accent,
                      backgroundColor: adminColors.accentLight
                    },
                    "&:active": {
                      transform: "scale(0.98)"
                    },
                    "&.Mui-focusVisible": {
                      borderColor: adminColors.accent,
                      boxShadow: `0 0 0 3px ${adminColors.accentBorder}`
                    }
                  }}
                >
                  <Box
                    sx={{
                      flexShrink: 0,
                      width: 32,
                      height: 32,
                      borderRadius: "8px",
                      // Kept white (not accentLight) so the tile stays
                      // visible when the card's own hover background turns
                      // accentLight — matching backgrounds would make the
                      // icon lose its edge right when the card is active.
                      // The border does the same job at rest, against the
                      // card's default white background.
                      backgroundColor: adminColors.surfaceRaised,
                      border: `1px solid ${adminColors.accentBorder}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    <Icon size={16} color={adminColors.accent} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: 14, color: adminColors.ink, mb: 0.25 }}>
                      {label}
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: adminColors.inkSoft, lineHeight: 1.4 }}>
                      {detail}
                    </Typography>
                  </Box>
                </ButtonBase>
              ))}
            </Box>
          </Box>
        ) : (
          // Chat messages
          chatHistory.map((msg, idx) => (
            <ChatMessage
              key={idx}
              msg={msg}
              idx={idx}
              editingMessageId={editingMessageId}
              editingContent={editingContent}
              setEditingContent={setEditingContent}
              onStartEdit={onStartEdit}
              onSaveEdit={onSaveEdit}
              onCancelEdit={onCancelEdit}
              onDelete={onDelete}
              onFileDelete={onFileDelete}
              onFileDownload={onFileDownload}
            />
          ))
        )}
      </Box>

      {/* "Jump to latest" - only when there's something to jump to and the
          reader has actually scrolled away from it. Floats just above the
          composer (this wrapper's own bottom edge, not the scrolling
          content's), so it never scrolls with the messages. */}
      {showJumpToLatest && (
        <ButtonBase
          onClick={onJumpToLatest}
          focusRipple
          aria-label={isStreamingNow ? "New messages, jump to latest" : "Jump to latest message"}
          sx={{
            position: "absolute",
            bottom: 16,
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            px: 2,
            py: 0.875,
            borderRadius: radius.pill,
            backgroundColor: adminColors.accent,
            color: "#fff",
            fontSize: 13,
            fontWeight: 600,
            boxShadow: "0 4px 14px rgba(79,70,229,0.35), 0 1px 2px rgba(24,24,27,0.08)",
            opacity: 0,
            animation: "jumpToLatestIn 180ms ease-out forwards",
            "@keyframes jumpToLatestIn": {
              from: { opacity: 0, transform: "translateX(-50%) translateY(6px)" },
              to: { opacity: 1, transform: "translateX(-50%) translateY(0)" },
            },
            "@media (prefers-reduced-motion: reduce)": {
              animation: "none",
              opacity: 1,
            },
            transition: "background-color 150ms ease, transform 120ms ease-out",
            "&:hover": { backgroundColor: adminColors.accentDark },
            "&:active": { transform: "translateX(-50%) scale(0.96)" },
            "&.Mui-focusVisible": {
              outline: `2px solid ${adminColors.accent}`,
              outlineOffset: 2,
            },
            ...TOUCH_HIT_SX,
          }}
        >
          {isStreamingNow ? "New messages" : "Latest"}
          <IoArrowDown size={13} />
        </ButtonBase>
      )}
    </Box>
  );
};

export default ChatArea;
